/**
 * Declarative role access for menus, routes, and feature surfaces.
 * Same rule as RequireRoles: user must have at least one listed role.
 * Use `hasAccess(key, userRoles)` from `../hasAccess`.
 *
 * Optional `requiresAsync: true` gates on user.is_async (see hasAccess context).
 */

/** Shared where coaching routes, nav, and unified chat filters must agree. */
export const COACHING_CHAT_ROLES = [
  "admin",
  "super-admin",
  "ops",
  "guide",
  "success_manager",
  "coach",
  "iop_therapist",
];

/** Roles that act as a coach inside a chat (participant rules, escalation actions). */
export const COACH_LIKE_ROLES = ["coach", "iop_therapist"];

/** Chat types a coach-like role may manage participants on. */
export const COACH_CHAT_TYPES = ["coaching", "iop"];

/**
 * @typedef {{ roles?: string[] | null, requiresAsync?: boolean }} AccessRule
 * @type {Record<string, AccessRule>}
 */
export const ACCESS = {
  purchases: { roles: ["admin", "ops", "doctor"] },
  customers: { roles: ["admin", "ops", "guide", "success_manager", "doctor"] },
  /** AppBar user search dropdown (GET /users/search). */
  globalUserSearch: {
    roles: ["admin", "ops", "guide", "success_manager", "doctor"],
  },
  chats: { roles: ["admin", "ops", "guide", "success_manager", "doctor"] },
  chatNav: { roles: ["admin", "ops", "guide", "success_manager", "doctor"] },
  auroraAudit: { roles: ["admin", "ops"] },
  prescriptionManagement: { roles: ["admin", "ops", "doctor"] },
  connect: { roles: ["admin", "ops", "sales", "guide", "doctor"] },
  icebergPatients: { roles: ["admin", "ops", "guide", "success_manager"] },
  iopLeads: { roles: ["admin", "ops", "sales", "guide", "iop_therapist"] },
  /** IOP patient chats (type iop). Separate from coaching chats. */
  iopChats: { roles: ["admin", "super-admin", "ops", "iop_therapist"] },
  katSubscriptionMgmt: { roles: ["admin", "ops", "doctor", "success_manager"] },
  scheduledActions: { roles: ["admin", "ops"] },
  consultationWaitlist: { roles: ["admin", "ops", "doctor"] },
  /** Ops/CS audit of open consults that appear stuck (pending labs, ID, payment, etc.). */
  stuckConsultations: {
    roles: ["admin", "super-admin", "ops", "customer_support"],
  },
  testKits: { roles: ["admin", "ops", "doctor"] },
  doctorRequests: { roles: ["admin", "ops", "doctor"] },
  linkShortener: { roles: ["admin", "ops", "sales", "guide"] },
  knowledgeBase: {
    roles: ["admin", "ops", "guide", "success_manager", "doctor", "sales"],
  },
  contentCreation: {
    roles: ["admin", "ops", "guide", "success_manager", "doctor", "sales"],
  },
  orders: { roles: ["admin", "ops"] },
  consultations: { roles: ["admin", "ops", "doctor"] },
  /** Doctor-only shortcuts to their own pending consults and chats. */
  myWorkQueues: { roles: ["doctor"] },
  insuranceConsults: { roles: ["admin", "ops", "doctor"] },
  salesDashboard: { roles: ["admin", "ops", "sales", "guide"] },
  liveSales: { roles: ["admin", "super-admin"] },
  formSubmissions: { roles: ["admin", "ops", "sales", "guide"] },
  p2pReferrals: { roles: ["admin", "ops", "sales"] },
  coachingChats: { roles: COACHING_CHAT_ROLES },
  coachingPatients: { roles: COACHING_CHAT_ROLES },
  coachingSessions: { roles: COACHING_CHAT_ROLES },
  groupSessions: { roles: COACHING_CHAT_ROLES },
  supportChats: {
    roles: [
      "experimental-features",
      "dev",
      "super-admin",
      "admin",
      "sales",
      "customer_support",
    ],
  },
  customerServiceDashboard: { roles: ["admin", "super-admin"] },
  chatConfigs: { roles: ["admin", "super-admin"] },
  clinicianChatHolds: { roles: ["admin", "super-admin"] },
  emailTester: { roles: ["admin", "super-admin"] },
  chatTools: { roles: ["admin", "super-admin"] },
  funnelBrowser: { roles: ["admin", "super-admin", "marketing"] },
  adminUsers: { roles: ["admin", "super-admin"] },
  /** CYH Brain scheduled routines (brain_routines). Super-admin only. */
  brainRoutines: { roles: ["super-admin"] },
  clinicians: { roles: ["admin"] },
  supportedStates: { roles: ["admin"] },
  planManagement: { roles: ["admin", "super-admin"] },
  adminReports: {
    roles: ["admin", "super-admin", "ops", "guide", "success_manager"],
  },
  prescriptionBalance: { roles: ["admin"] },
  coachBlacklist: { roles: ["admin", "super-admin"] },
  testimonials: { roles: ["admin", "super-admin", "ops", "marketing"] },
  contentCapture: { roles: ["admin", "super-admin", "ops", "marketing"] },
  contentCaptureMy: {
    roles: [
      "admin",
      "super-admin",
      "ops",
      "marketing",
      "doctor",
      "coach",
      "guide",
      "success_manager",
    ],
  },
};
